import React, { useEffect, useState, useContext } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate, useParams } from "react-router-dom";
import NotFound from "../notfound/NotFound";
import {
  editDeposit,
  getDepositById,
  getAllBranchOfficesByBu,
  getAllBranchOffices,
  getAllDeposits,
  getCategoriesFiltered,
  processExcelTemplate,
  getHistoricalStockDeposit,
  addScheduleToDeposit,
  deleteScheduleFromDeposit,
  getAllRecurrencesTypes,
  getVehicles,
  getDepositsSkusById,
  getDepositSchedulesById,
  getDepositValuedInventory,
} from "../../api/Services";
import { BusinessUniteContext } from "../../components/layout/Layout";
import { hasPermission, privilegesEnum } from "../../helpers/PermissionHelper";
import ArticleStock from "../../components/articlestock/ArticleStock";

import {
  Form,
  message,
  Select,
  Button,
  Input,
  Spin,
  TreeSelect,
  InputNumber,
  Modal,
  DatePicker,
  Table,
  Space,
  Cascader,
  Checkbox,
  TimePicker,
} from "antd";
import {
  LoadingOutlined,
  PlusOutlined,
  DeleteOutlined,
  ExclamationCircleOutlined,
} from "@ant-design/icons";

import styles from "./ViewDeposit.module.scss";
import { handleDownloadTemplate } from "../../helpers/Helper";
import ImportModal from "../../components/importmodal/ImportModal";

const ViewDeposit = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const {
    contextValue: businessUnitSelected,
    updateContextValue: setBusinessUnitSelected,
  } = useContext(BusinessUniteContext);

  const [loading, setLoading] = useState(true);
  const [deposit, setDeposit] = useState<any>();
  const [categories, setCategories] = useState<any>();
  const [downloadingTemplate, setDownloadingTemplate] = useState(false);
  const [isImportModalVisible, setIsImportModalVisible] = useState(false);
  const [importErrors, setImportErrors] = useState<any>(null);
  const [branchOfficesSelected, setBranchOfficesSelected] = useState<any[]>([]);
  const [historicalStockActualPage, setHistoricalStockActualPage] = useState(1);
  const [totalSizeHistoricalStock, setTotalSizeHistoricalStock] = useState(0);
  const [historicalStocks, setHistoricalStock] = useState<any[]>([]);
  const [selectedDates, setSelectedDates] = useState<[any, any] | null>(null);
  const [isAddScheduleModalVisible, setIsAddScheduleModalVisible] =
    useState(false);
  const [vehicles, setVehicles] = useState<any[]>([]);
  const [recurrenceTypes, setRecurrenceTypes] = useState<any[]>([]);
  const [allBranchOffices, setAllBranchOffices] = useState<any[]>([]);
  const [allDeposits, setAllDeposits] = useState<any[]>([]);

  const [depositSkus, setDepositSkus] = useState<any[]>([]);
  const [depositSkusPage, setDepositSkusPage] = useState(1);
  const [depositSkusTotal, setDepositSkusTotal] = useState(0);
  const [loadingDepositSkus, setLoadingDepositSkus] = useState(false);

  const [depositSchedules, setDepositSchedules] = useState<any[]>([]);
  const [depositSchedulesPage, setDepositSchedulesPage] = useState(1);
  const [depositSchedulesTotal, setDepositSchedulesTotal] = useState(0);
  const [loadingDepositSchedules, setLoadingDepositSchedules] = useState(false);

  const [valuedInventory, setValuedInventory] = useState<number | undefined>(
    undefined
  );
  const [loadingValuedInventory, setLoadingValuedInventory] = useState(false);

  const [editForm] = Form.useForm();
  const [addScheduleForm] = Form.useForm();

  const canEdit = hasPermission(privilegesEnum.WRITE_DEPOSITS);

  const { id } = useParams();

  useEffect(() => {
    if (hasPermission(privilegesEnum.READ_DEPOSITS)) {
      handleFetchDeposit();
      handleFetchDepositSkus();
      handleFetchDepositSchedules();
      handleFetchValuedInventory();
      handleFetchVehicles();
      handleFetchRecurrenceTypes();
      handleFetchAllBranchOffices();
      handleFetchAllDeposits();
    }
  }, []);

  useEffect(() => {
    if (hasPermission(privilegesEnum.READ_DEPOSITS)) {
      handleFetchHistoricalStock();
    }
  }, [selectedDates]);

  const handleFetchHistoricalStock = async (
    pagination = historicalStockActualPage
  ) => {
    const params: any = {};

    if (selectedDates) {
      const [startDate, endDate] = selectedDates;
      if (startDate && endDate) {
        params.startDate = startDate.format("YYYY-MM-DD");
        params.endDate = endDate.format("YYYY-MM-DD");
      }
    }
    const response = await getHistoricalStockDeposit(id!, pagination, {
      startDate: params.startDate,
      endDate: params.endDate,
    });
    setHistoricalStockActualPage(pagination);
    setTotalSizeHistoricalStock(response.totalElements);
    setHistoricalStock(response.stocks);
  };

  const handleFetchDepositSkus = async (pagination = depositSkusPage) => {
    if (!id) return;
    setLoadingDepositSkus(true);
    try {
      const response = await getDepositsSkusById(id, pagination - 1, "");
      setDepositSkus(response.skus || []);
      setDepositSkusTotal(response.totalElements || 0);
      setDepositSkusPage(pagination);
    } finally {
      setLoadingDepositSkus(false);
    }
  };

  const handleFetchDepositSchedules = async (
    pagination = depositSchedulesPage
  ) => {
    if (!id) return;
    setLoadingDepositSchedules(true);
    try {
      const response = await getDepositSchedulesById(id, pagination - 1);
      setDepositSchedules(response.schedules || []);
      setDepositSchedulesTotal(response.totalElements || 0);
      setDepositSchedulesPage(pagination);
    } finally {
      setLoadingDepositSchedules(false);
    }
  };

  useEffect(() => {
    if (
      deposit &&
      !deposit.businessUnits
        .map((businessUnit: any) => businessUnit.id)
        .includes(businessUnitSelected) &&
      businessUnitSelected &&
      !loading
    ) {
      navigate("/admin/deposits");
    }
  }, [businessUnitSelected]);

  const getOptionsCategories = (items: any) => {
    const options: any[] = [];

    items?.forEach(function (item: any) {
      const parentsString = item?.parents
        ? item.parents.filter(Boolean).join(" - ")
        : "";
      const categoryName = item?.name || "";

      const categoryTree = parentsString
        ? `${parentsString} - ${categoryName}`
        : categoryName;

      options.push(
        <Select.Option value={item.id} key={item.id}>
          {categoryTree}
        </Select.Option>
      );
    });

    return options;
  };
  const getOptionsTree = (items: any) => {
    const options: any[] = [];

    items?.forEach(function (item: any) {
      options.push({
        title: item.businessUnit.name,
        value: item.businessUnit.name,
        key: item.businessUnit.name,
        children: item.branchOffices.map((bu: any) => ({
          title: bu.name,
          value: bu.id,
          key: bu.id,
        })),
      });
    });

    return options;
  };

  const handleSearchCategories = async (value: string) => {
    if (value.length > 2) {
      const response = await getCategoriesFiltered(value);
      setCategories(response);
    } else setCategories([]);
  };
  const handleFetchDeposit = async () => {
    let response = await getDepositById(id!);

    if (response.businessUnits.length > 0) {
      const branchOffices = await getAllBranchOfficesByBu(
        response.businessUnits.map((bu: any) => bu.id)
      );
      response.branchOfficesOptions = branchOffices;
    }
    setBranchOfficesSelected(
      response.branchOffices.map((item: any) => ({
        id: item.branchOffice.id,
        name: item.branchOffice.name,
      }))
    );

    setCategories(response.categories);
    setDeposit(response);
    setBusinessUnitSelected(response.businessUnits[0].id);
    setLoading(false);
  };

  const handleFetchValuedInventory = async () => {
    if (!id) return;
    setLoadingValuedInventory(true);
    try {
      const value = await getDepositValuedInventory(id);
      setValuedInventory(value);
      editForm.setFieldsValue({ valuedInventory: value });
    } finally {
      setLoadingValuedInventory(false);
    }
  };

  const handleEditDeposit = async () => {
    await editForm.validateFields().then(async (data) => {
      setLoading(true);
      const id = deposit.id;
      const {
        name,
        externalId,
        capacity,
        branchOfficesIds,
        address,
        categoriesIds,
        capacityInUnits,
        capacityDispatchUnits,
        dispatchFrequency,
        capacityReception,
        storageCostPerUnit,
        minimumStock,
        maximumStock,
        latitude,
        longitude,
      } = data;

      const branchOfficesDeposit = branchOfficesIds?.map((id: any) => ({
        branchOfficeId: id,
        distance: data[`distance-${id}`],
      }));

      let response = await editDeposit(id, {
        name,
        capacity,
        externalId,
        branchOffices: branchOfficesDeposit,
        address,
        businessUnitsIds: [businessUnitSelected],
        categoriesIds: categoriesIds ?? [],
        capacityInUnits,
        capacityDispatchUnits,
        dispatchFrequency,
        capacityReception,
        storageCostPerUnit,
        minimumStock,
        maximumStock,
        latitude,
        longitude,
      });

      if (response) {
        message.success(t("Common-EditSuccessMessage"));
        handleFetchDeposit();
      } else message.error(t("Common-EditErrorMessage"));
      setLoading(false);
    });
  };

  const handleCancelButton = () => {
    editForm.resetFields();
  };

  const showImportModal = () => {
    setIsImportModalVisible(true);
    setImportErrors("");
  };

  const handleCancelImportModal = () => {
    setIsImportModalVisible(false);
  };

  const excelButtons = (
    <>
      <Button
        onClick={showImportModal}
        className={styles.importButton}
        type="primary"
      >
        {t("Common-Import")}
      </Button>
      <Button
        onClick={() =>
          handleDownloadTemplate("deposits/stocks", setDownloadingTemplate)
        }
        loading={downloadingTemplate}
        className={styles.importButton}
        type="primary"
      >
        {t("Common-DownloadTemplate")}
      </Button>
    </>
  );

  const handleImportFile = async (file: any) => {
    if (file) {
      const response = await processExcelTemplate("branchOffices/stocks", file);
      if (response.success) {
        setImportErrors(null);
        setIsImportModalVisible(false);
        message.success(
          t("Common-ImportSuccess", { quantity: response.data.added })
        );
        handleFetchDeposit();
      } else {
        if (response.data?.errors) setImportErrors(response.data.errors);
        else message.error(t("Error-CommonError"));
      }
    }
  };

  const renderBranchOfficeDistanceFields = () => {
    return branchOfficesSelected.map((branchOfficeOption: any) => {
      let foundBranchOffice: any = null;

      deposit.branchOffices.some((boDeposit: any) => {
        if (boDeposit.branchOffice.id === branchOfficeOption.id)
          foundBranchOffice = boDeposit;
        return !!foundBranchOffice;
      });
      return (
        <Form.Item
          key={branchOfficeOption.id}
          label={`${t("Common-Distance")} ${branchOfficeOption.name}`}
          name={`distance-${branchOfficeOption.id}`}
          initialValue={foundBranchOffice?.distance}
        >
          <InputNumber placeholder={t("Common-Distance")} />
        </Form.Item>
      );
    });
  };

  const handleBranchOfficeChange = (
    selectedBranchOfficeIds: any,
    labelList: any
  ) => {
    const selectedBranchOffices = selectedBranchOfficeIds.map(
      (id: any, index: number) => ({
        id: id,
        name: labelList[index],
      })
    );

    setBranchOfficesSelected(selectedBranchOffices);

    const currentValues = editForm.getFieldsValue();
    const updatedValues = { ...currentValues };

    branchOfficesSelected.forEach((officeId) => {
      if (!selectedBranchOfficeIds.includes(officeId)) {
        delete updatedValues[`distance-${officeId}`];
      }
    });

    editForm.setFieldsValue(updatedValues);
  };

  const handleFetchVehicles = async () => {
    const response = await getVehicles(1, {});
    setVehicles(response.vehicles);
  };

  const handleFetchRecurrenceTypes = async () => {
    const response = await getAllRecurrencesTypes();
    if (response) {
      setRecurrenceTypes(response);
    }
  };

  const handleFetchAllBranchOffices = async () => {
    const response = await getAllBranchOffices();
    if (response) {
      setAllBranchOffices(response);
    }
  };

  const handleFetchAllDeposits = async () => {
    const response = await getAllDeposits();
    if (response) {
      setAllDeposits(response);
    }
  };

  const handleOpenAddScheduleModal = () => {
    setIsAddScheduleModalVisible(true);
  };

  const handleCancelAddScheduleModal = () => {
    setIsAddScheduleModalVisible(false);
  };

  const handleAddSchedule = async () => {
    await addScheduleForm.validateFields().then(async (data) => {
      setLoading(true);

      const {
        code,
        description,
        recurrenceType,
        monday,
        tuesday,
        wednesday,
        thursday,
        friday,
        saturday,
        sunday,
        dayMonthly,
        dayAnually,
        startTime,
        vehicleId,
        branchOfficeIdTo,
        depositIdTo,
      } = data;

      const recurrence: {
        type: any;
        dayAnually?: any;
        dayMonthly?: any;
        monday?: boolean;
        tuesday?: boolean;
        wednesday?: boolean;
        thursday?: boolean;
        friday?: boolean;
        saturday?: boolean;
        sunday?: boolean;
        startTime?: any;
        endTime?: any;
      } = {
        type: recurrenceType,
        startTime:
          startTime && startTime[0] ? startTime[0].format("HH:mm") : null,
        endTime:
          startTime && startTime[1] ? startTime[1].format("HH:mm") : null,
      };

      if (recurrenceType === "WEEKLY") {
        recurrence.monday = monday;
        recurrence.tuesday = tuesday;
        recurrence.wednesday = wednesday;
        recurrence.thursday = thursday;
        recurrence.friday = friday;
        recurrence.saturday = saturday;
        recurrence.sunday = sunday;
      } else if (recurrenceType === "MONTHLY") {
        recurrence.dayMonthly = dayMonthly;
      } else if (recurrenceType === "ANUALLY") {
        recurrence.dayAnually = dayAnually
          ? dayAnually.format("YYYY-MM-DD")
          : null;
      }

      const response = await addScheduleToDeposit(parseInt(id!), {
        code,
        description,
        depositIdTo,
        branchOfficeIdTo,
        vehicleId,
        recurrence,
      });

      if (response) {
        addScheduleForm.resetFields();
        await handleFetchDepositSchedules();
        handleCancelAddScheduleModal();
        message.success(t("Common-AddSuccessMessage"));
      } else message.error(t("Common-ErrorMessage"));
      setLoading(false);
    });
  };

  const showDeleteScheduleConfirm = (scheduleId: number) => {
    Modal.confirm({
      title: t("Common-DeleteScheduleConfirm"),
      icon: <ExclamationCircleOutlined />,
      content: "",
      okText: t("Common-Yes"),
      okType: "danger",
      cancelText: t("Common-No"),
      onOk() {
        handleDeleteSchedule(scheduleId);
      },
    });
  };

  const handleDeleteSchedule = async (scheduleId: number) => {
    setLoading(true);
    const deleteStatus = await deleteScheduleFromDeposit(
      parseInt(id!),
      scheduleId
    );
    if (!deleteStatus) message.error(t("Common-DeleteErrorMessage"));
    else message.success(t("Common-DeleteSuccessMessage"));
    await handleFetchDepositSchedules();
    setLoading(false);
  };

  const getRecurrenceOptions = () => {
    const options: any[] = [];

    recurrenceTypes.forEach(function (item: any) {
      options.push(
        <Select.Option value={item.value} key={item.value}>
          {item.description_es}
        </Select.Option>
      );
    });

    return options;
  };

  const getOptions = (items: any) => {
    const options: any[] = [];

    items.forEach(function (item: any) {
      options.push(
        <Select.Option value={item.id} key={item.id}>
          {item.name}
        </Select.Option>
      );
    });

    return options;
  };

  const editedBranchOffices: number[] = [];
  deposit?.branchOffices?.forEach((branchOffice: any) =>
    editedBranchOffices.push(branchOffice.branchOffice.id)
  );

  const historicalStockColumns = [
    {
      title: t("Common-Sku"),
      dataIndex: "sku",
      render: (sku: any) => sku.name,
    },
    {
      title: t("Common-Date"),
      dataIndex: "date",
      render: (date: string) => date.split("T")[0],
    },
    {
      title: t("Common-Stock"),
      dataIndex: "stock",
    },
  ];

  const schedulesColumns = [
    {
      title: t("Common-Code"),
      dataIndex: "code",
    },
    {
      title: t("Common-Description"),
      dataIndex: "description",
    },
    {
      title: t("Common-Destination"),
      key: "destiny",
      render: (record: any) => {
        if (record.branchOfficeTo) {
          return (
            <a href={`/admin/branchoffices/${record.branchOfficeTo.id}/view`}>
              {record.branchOfficeTo.name}
            </a>
          );
        } else if (record.depositTo) {
          return (
            <a href={`/admin/deposits/${record.depositTo.id}/view`}>
              {record.depositTo.name}
            </a>
          );
        }
        return "-";
      },
    },
    {
      title: t("Common-Vehicle"),
      key: "vehicle",
      render: (record: any) => record.vehicle?.name || "-",
    },
    {
      title: t("Common-Type"),
      dataIndex: "type",
      render: (type: string) => {
        if (!type) return "-";
        switch (type) {
          case "MONTHLY":
            return t("Common-Monthly");
          case "WEEKLY":
            return t("Common-Weekly");
          case "ANUALLY":
            return t("Common-Anually");
          default:
            return type;
        }
      },
    },
    {
      title: t("Common-StartTime"),
      dataIndex: "startTime",
      render: (time: any) => time || "-",
    },
    {
      title: t("Common-EndTime"),
      dataIndex: "endTime",
      render: (time: any) => time || "-",
    },
    {
      title: t("Common-Actions"),
      key: "action",
      render: (record: any) => (
        <Space size="middle">
          <DeleteOutlined
            onClick={() => {
              showDeleteScheduleConfirm(record.id);
            }}
          />
        </Space>
      ),
    },
  ];

  return loading ? (
    <Spin
      indicator={<LoadingOutlined className="ol-loading-spin-icon" />}
      delay={500}
    />
  ) : !deposit ? (
    <NotFound />
  ) : (
    <div className={styles.wrapper}>
      <div className={styles.header}>
        <h1 className={styles.title}>{t("Common-Deposit")}</h1>
        {hasPermission(privilegesEnum.WRITE_DEPOSITS) && (
          <div className={styles.actionButtonsWrapper}>
            <Button
              onClick={handleEditDeposit}
              className={styles.saveButton}
              type="primary"
            >
              {t("Common-Save")}
            </Button>
            <Button
              onClick={handleCancelButton}
              className={styles.cancelButton}
              type="primary"
            >
              {t("Common-Cancel")}
            </Button>
          </div>
        )}
      </div>

      <div className={styles.contentWrapper}>
        <div className={styles.depositInfoWrapper}>
          <Form
            name="viewdeposit"
            layout={"vertical"}
            initialValues={{ remember: true }}
            autoComplete="off"
            className={styles.newForm}
            form={editForm}
          >
            <Form.Item
              label={t("Common-Name")}
              name="name"
              initialValue={deposit?.name}
              className={styles.formItem}
              rules={[
                {
                  required: true,
                  message: t("Common-NameNotEntered"),
                },
              ]}
            >
              <Input disabled={!canEdit} placeholder={t("Common-Name")} />
            </Form.Item>
            <Form.Item
              label={t("Common-ValuedInventory")}
              name="valuedInventory"
              className={styles.formItem}
            >
              <Input
                disabled={true}
                placeholder={
                  loadingValuedInventory
                    ? t("Common-Loading")
                    : t("Common-ValuedInventory")
                }
              />
            </Form.Item>

            <Form.Item
              label={t("Common-CapacityStorageInmt3")}
              name="capacity"
              initialValue={deposit?.capacity}
              className={styles.formItem}
            >
              <InputNumber
                disabled={!canEdit}
                placeholder={t("Common-CapacityStorageInmt3")}
                addonAfter={t("Common-mtcube")}
                className={styles.inputNumber}
              />
            </Form.Item>

            <Form.Item
              initialValue={deposit?.capacityInUnits}
              className={styles.formItem}
              label={t("Common-CapacityStorageInUnits")}
              name="capacityInUnits"
            >
              <InputNumber
                disabled={!canEdit}
                placeholder={t("Common-CapacityStorageInUnits")}
                className={styles.inputNumber}
              />
            </Form.Item>
            <Form.Item
              initialValue={deposit?.capacityDispatchUnits}
              className={styles.formItem}
              label={t("Common-CapacityDispatchUnits")}
              name="capacityDispatchUnits"
            >
              <InputNumber
                disabled={!canEdit}
                placeholder={t("Common-CapacityDispatchUnits")}
                className={styles.inputNumber}
              />
            </Form.Item>
            <Form.Item
              initialValue={deposit?.capacityReception}
              className={styles.formItem}
              label={t("Common-CapacityReception")}
              name="capacityReception"
            >
              <InputNumber
                disabled={!canEdit}
                placeholder={t("Common-CapacityReception")}
                className={styles.inputNumber}
              />
            </Form.Item>
            <Form.Item
              initialValue={deposit?.storageCostPerUnit}
              className={styles.formItem}
              label={t("Common-StorageCostPerUnit")}
              name="storageCostPerUnit"
            >
              <InputNumber
                disabled={!canEdit}
                placeholder={t("Common-StorageCostPerUnit")}
                className={styles.inputNumber}
              />
            </Form.Item>
            <Form.Item
              label={t("Common-MinimumStock")}
              name="minimumStock"
              initialValue={deposit?.minimumStock}
              className={styles.formItem}
            >
              <InputNumber
                placeholder={t("Common-MinimumStock")}
                className={styles.inputNumber}
              />
            </Form.Item>
            <Form.Item
              label={t("Common-MaximumStock")}
              name="maximumStock"
              initialValue={deposit?.maximumStock}
              className={styles.formItem}
            >
              <InputNumber
                placeholder={t("Common-MaximumStock")}
                className={styles.inputNumber}
              />
            </Form.Item>
            <Form.Item
              initialValue={deposit?.dispatchFrequency}
              className={styles.formItem}
              label={t("Common-DispatchFrequency")}
              name="dispatchFrequency"
            >
              <InputNumber
                disabled={!canEdit}
                min={1}
                max={7}
                placeholder={t("Common-DispatchFrequency")}
                className={styles.inputNumber}
              />
            </Form.Item>

            <Form.Item
              label={t("Common-ExternalId")}
              name="externalId"
              initialValue={deposit?.externalId}
              className={styles.formItem}
            >
              <Input placeholder={t("Common-ExternalId")} />
            </Form.Item>

            <Form.Item
              label={t("Common-Address")}
              name="address"
              initialValue={deposit?.address}
              className={styles.formItem}
            >
              <Input placeholder={t("Common-Address")} />
            </Form.Item>
            <Form.Item
              label={t("Common-Latitude")}
              name="latitude"
              initialValue={deposit?.latitude}
              className={styles.formItem}
            >
              <InputNumber
                className={styles.inputNumber}
                placeholder={t("Common-Latitude")}
              />
            </Form.Item>
            <Form.Item
              label={t("Common-Longitude")}
              name="longitude"
              initialValue={deposit?.longitude}
              className={styles.formItem}
            >
              <InputNumber
                className={styles.inputNumber}
                placeholder={t("Common-Longitude")}
              />
            </Form.Item>
            <Form.Item
              className={styles.formItem}
              label={t("Common-BranchOffices")}
              name="branchOfficesIds"
              initialValue={editedBranchOffices}
            >
              <TreeSelect
                allowClear
                placeholder={t("Common-BranchOffices")}
                treeCheckable={true}
                defaultValue={branchOfficesSelected}
                onChange={handleBranchOfficeChange}
                treeData={getOptionsTree(deposit?.branchOfficesOptions)}
              />
            </Form.Item>
            <div className={styles.distanceWrapper}>
              {renderBranchOfficeDistanceFields()}
            </div>
            <Form.Item
              label={t("Common-Categories")}
              name="categoriesIds"
              className={styles.formItem}
              initialValue={deposit?.categories?.map((cat: any) => cat.id)}
            >
              <Select
                allowClear
                placeholder={t("Common-SearchByName")}
                onSearch={handleSearchCategories}
                showSearch
                mode="multiple"
                optionFilterProp="children"
                defaultValue={deposit?.categories?.map((cat: any) => cat.id)}
                filterOption={(input: string, option: any) => {
                  const children = option?.children;
                  if (typeof children === "string") {
                    return (
                      children.toLowerCase().indexOf(input.toLowerCase()) >= 0
                    );
                  }
                  return false;
                }}
              >
                {getOptionsCategories(categories)}
              </Select>
            </Form.Item>
          </Form>
        </div>
        <div className={styles.tableWrapper}>
          <div className={styles.tablesHeader}>
            <h1 className={styles.tableTitle}>{t("Common-Schedules")}</h1>
            {canEdit && (
              <Button
                type="primary"
                className={styles.actionsButton}
                onClick={handleOpenAddScheduleModal}
                icon={<PlusOutlined />}
              />
            )}
          </div>
          <div>
            <Table
              size="small"
              className={styles.table}
              loading={loadingDepositSchedules}
              rowKey={(record) => record.id}
              columns={schedulesColumns}
              dataSource={depositSchedules}
              pagination={{
                total: depositSchedulesTotal,
                showSizeChanger: false,
                onChange: handleFetchDepositSchedules,
                current: depositSchedulesPage,
              }}
            />
          </div>
        </div>
        <ArticleStock
          data={depositSkus}
          header={excelButtons}
          fetchData={handleFetchDepositSkus}
          type={"deposits"}
          placeId={parseInt(id!)}
          totalElements={depositSkusTotal}
          updatePage={setDepositSkusPage}
          searchByName={false}
          page={depositSkusPage}
          loadingPage={loadingDepositSkus}
        />
        <div className={styles.tableWrapper}>
          <div className={styles.tablesHeader}>
            <h1 className={styles.tableTitle}>{t("Common-HistoricalStock")}</h1>
          </div>
          <div className={styles.filtersWrapper}>
            <div className={styles.filterWrapper}>
              <DatePicker.RangePicker
                className={styles.dateSelector}
                placeholder={[t("Common-Start"), t("Common-End")]}
                onChange={(dates) => setSelectedDates(dates)}
              ></DatePicker.RangePicker>
            </div>
          </div>
          <div>
            <Table
              size="small"
              className={styles.table}
              loading={loading}
              rowKey={(record) => record.id}
              columns={historicalStockColumns}
              pagination={{
                total: totalSizeHistoricalStock,
                showSizeChanger: false,
                onChange: handleFetchHistoricalStock,
                current: historicalStockActualPage,
              }}
              dataSource={historicalStocks}
            />
          </div>
        </div>
      </div>
      <Modal
        title={t("Common-ImportEntity", {
          entity: t("Common-Stock"),
        })}
        visible={isImportModalVisible}
        okButtonProps={{ style: { display: "none" } }}
        cancelButtonProps={{ style: { display: "none" } }}
        onCancel={handleCancelImportModal}
      >
        <ImportModal onOk={handleImportFile} errors={importErrors} />
      </Modal>

      {/* Add Schedule Modal */}
      <Modal
        title={t("Common-Transfer")}
        visible={isAddScheduleModalVisible}
        onCancel={handleCancelAddScheduleModal}
        footer={null}
      >
        <Form
          form={addScheduleForm}
          onFinish={handleAddSchedule}
          layout="vertical"
        >
          <Form.Item
            name="code"
            label={t("Common-Code")}
            rules={[
              {
                required: true,
                message: t("Common-CodeNotEntered"),
              },
            ]}
          >
            <InputNumber
              style={{ width: "100%" }}
              placeholder={t("Common-Code")}
            />
          </Form.Item>

          <Form.Item
            name="description"
            label={t("Common-Description")}
            rules={[
              {
                required: true,
                message: t("Common-DescriptionNotEntered"),
              },
            ]}
          >
            <Input placeholder={t("Common-Description")} />
          </Form.Item>

          <Form.Item
            name="branchOfficeIdTo"
            label={t("Common-DestinationBranchOffice")}
          >
            <Select
              allowClear
              placeholder={t("Common-SelectDestinationBranchOffice")}
              showSearch
              filterOption={(input, option) => {
                const children = option?.children as any;
                if (typeof children === "string") {
                  return (
                    children.toLowerCase().indexOf(input.toLowerCase()) >= 0
                  );
                }
                if (Array.isArray(children)) {
                  return (
                    children
                      .join("")
                      .toLowerCase()
                      .indexOf(input.toLowerCase()) >= 0
                  );
                }
                return (
                  String(children || "")
                    .toLowerCase()
                    .indexOf(input.toLowerCase()) >= 0
                );
              }}
            >
              {getOptions(allBranchOffices)}
            </Select>
          </Form.Item>

          <Form.Item name="depositIdTo" label={t("Common-DestinationDeposit")}>
            <Select
              allowClear
              placeholder={t("Common-SelectDestinationDeposit")}
              showSearch
              filterOption={(input, option) => {
                const children = option?.children as any;
                if (typeof children === "string") {
                  return (
                    children.toLowerCase().indexOf(input.toLowerCase()) >= 0
                  );
                }
                if (Array.isArray(children)) {
                  return (
                    children
                      .join("")
                      .toLowerCase()
                      .indexOf(input.toLowerCase()) >= 0
                  );
                }
                return (
                  String(children || "")
                    .toLowerCase()
                    .indexOf(input.toLowerCase()) >= 0
                );
              }}
            >
              {getOptions(allDeposits)}
            </Select>
          </Form.Item>

          <Form.Item
            name="vehicleId"
            label={t("Common-Vehicle")}
            rules={[
              {
                required: true,
                message: t("Common-VehicleNotSelected"),
              },
            ]}
          >
            <Select
              placeholder={t("Common-SelectVehicle")}
              showSearch
              filterOption={(input, option) => {
                const children = option?.children as any;
                if (typeof children === "string") {
                  return (
                    children.toLowerCase().indexOf(input.toLowerCase()) >= 0
                  );
                }
                if (Array.isArray(children)) {
                  return (
                    children
                      .join("")
                      .toLowerCase()
                      .indexOf(input.toLowerCase()) >= 0
                  );
                }
                return (
                  String(children || "")
                    .toLowerCase()
                    .indexOf(input.toLowerCase()) >= 0
                );
              }}
            >
              {getOptions(vehicles)}
            </Select>
          </Form.Item>

          <Form.Item
            name="recurrenceType"
            label={t("Common-Recurrence")}
            rules={[
              {
                required: true,
                message: t("Common-RecurrenceNotSelected"),
              },
            ]}
          >
            <Select
              placeholder={t("Common-SelectRecurrence")}
              onChange={(value) => {
                addScheduleForm.setFieldsValue({
                  monday: false,
                  tuesday: false,
                  wednesday: false,
                  thursday: false,
                  friday: false,
                  saturday: false,
                  sunday: false,
                  dayMonthly: undefined,
                  dayAnually: undefined,
                });
              }}
            >
              {getRecurrenceOptions()}
            </Select>
          </Form.Item>

          {/* Weekly recurrence fields */}
          <Form.Item
            noStyle
            shouldUpdate={(prevValues, currentValues) =>
              prevValues.recurrenceType !== currentValues.recurrenceType
            }
          >
            {({ getFieldValue }) => {
              const recurrenceType = getFieldValue("recurrenceType");
              if (recurrenceType === "WEEKLY") {
                return (
                  <div
                    style={{ display: "flex", flexWrap: "wrap", gap: "8px" }}
                  >
                    <Form.Item
                      name="monday"
                      valuePropName="checked"
                      style={{ marginBottom: "8px" }}
                    >
                      <Checkbox>{t("Common-Monday")}</Checkbox>
                    </Form.Item>
                    <Form.Item
                      name="tuesday"
                      valuePropName="checked"
                      style={{ marginBottom: "8px" }}
                    >
                      <Checkbox>{t("Common-Tuesday")}</Checkbox>
                    </Form.Item>
                    <Form.Item
                      name="wednesday"
                      valuePropName="checked"
                      style={{ marginBottom: "8px" }}
                    >
                      <Checkbox>{t("Common-Wednesday")}</Checkbox>
                    </Form.Item>
                    <Form.Item
                      name="thursday"
                      valuePropName="checked"
                      style={{ marginBottom: "8px" }}
                    >
                      <Checkbox>{t("Common-Thursday")}</Checkbox>
                    </Form.Item>
                    <Form.Item
                      name="friday"
                      valuePropName="checked"
                      style={{ marginBottom: "8px" }}
                    >
                      <Checkbox>{t("Common-Friday")}</Checkbox>
                    </Form.Item>
                    <Form.Item
                      name="saturday"
                      valuePropName="checked"
                      style={{ marginBottom: "8px" }}
                    >
                      <Checkbox>{t("Common-Saturday")}</Checkbox>
                    </Form.Item>
                    <Form.Item
                      name="sunday"
                      valuePropName="checked"
                      style={{ marginBottom: "8px" }}
                    >
                      <Checkbox>{t("Common-Sunday")}</Checkbox>
                    </Form.Item>
                  </div>
                );
              }
              return null;
            }}
          </Form.Item>

          {/* Monthly recurrence field */}
          <Form.Item
            noStyle
            shouldUpdate={(prevValues, currentValues) =>
              prevValues.recurrenceType !== currentValues.recurrenceType
            }
          >
            {({ getFieldValue }) => {
              const recurrenceType = getFieldValue("recurrenceType");
              if (recurrenceType === "MONTHLY") {
                return (
                  <Form.Item
                    name="dayMonthly"
                    label={t("Common-DayOfMonth")}
                    rules={[
                      {
                        required: true,
                        message: t("Common-DayOfMonthNotEntered"),
                      },
                    ]}
                  >
                    <InputNumber
                      min={1}
                      max={31}
                      style={{ width: "100%" }}
                      placeholder={t("Common-DayOfMonth")}
                    />
                  </Form.Item>
                );
              }
              return null;
            }}
          </Form.Item>

          {/* Annually recurrence field */}
          <Form.Item
            noStyle
            shouldUpdate={(prevValues, currentValues) =>
              prevValues.recurrenceType !== currentValues.recurrenceType
            }
          >
            {({ getFieldValue }) => {
              const recurrenceType = getFieldValue("recurrenceType");
              if (recurrenceType === "ANUALLY") {
                return (
                  <Form.Item
                    name="dayAnually"
                    label={t("Common-DayOfYear")}
                    rules={[
                      {
                        required: true,
                        message: t("Common-DayOfYearNotEntered"),
                      },
                    ]}
                  >
                    <DatePicker
                      style={{ width: "100%" }}
                      placeholder={t("Common-DayOfYear")}
                    />
                  </Form.Item>
                );
              }
              return null;
            }}
          </Form.Item>

          {/* Time field */}
          <Form.Item name="startTime" label={t("Common-Time")}>
            <TimePicker.RangePicker
              style={{ width: "100%" }}
              placeholder={["Hora inicio", "Hora fin"]}
              format="HH:mm"
            />
          </Form.Item>

          <Form.Item>
            <Space>
              <Button type="primary" htmlType="submit" loading={loading}>
                {t("Common-Add")}
              </Button>
              <Button onClick={handleCancelAddScheduleModal}>
                {t("Common-Cancel")}
              </Button>
            </Space>
          </Form.Item>
        </Form>
      </Modal>
    </div>
  );
};

export default ViewDeposit;
